import { useColorMode, useMediaQuery } from "@chakra-ui/react";
import { GetProgrammaticCampaignIntegratedCampaignReportRO } from "@intentsify/dto";
import {
  BuyerResearchStage,
  BuyerResearchStageMap,
  IntegratedCampaignReportReached,
} from "@intentsify/types";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  MIN_MODAL_CONTENT_HEIGHT,
  StackedGroupedBarChart,
  getChartColors,
} from "components";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import groupBy from "lodash/groupBy";
import partition from "lodash/partition";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { MdAnalytics } from "react-icons/md";
import { useExportableChart } from "shared/components";
import { DisplayPerformanceFilters } from "../../../../types";

type IntegratedCampaignReportProps = {
  campaignId: string;
  filters: DisplayPerformanceFilters;
  getIntegratedCampaignReport: (
    id: string,
    filters: DisplayPerformanceFilters
  ) => Promise<GetProgrammaticCampaignIntegratedCampaignReportRO>;
};

const TITLE = "Integrated Campaign Report";

const IntegratedCampaignReport = (props: IntegratedCampaignReportProps) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <IntegratedCampaignReportContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const IntegratedCampaignReportContent = ({
  campaignId,
  filters,
  getIntegratedCampaignReport,
}: IntegratedCampaignReportProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { colorMode } = useColorMode();
  const [isLargerThan1800] = useMediaQuery("(min-width: 1800px)");

  const {
    data: integratedCampaignReportData,
    isFetching: isLoadingCampaignIntegratedReportData,
  } = useQuery(["campaignIntegratedReport", campaignId, filters], () =>
    getIntegratedCampaignReport(campaignId, filters)
  );

  const dates = useMemo(() => {
    return {
      start: DateTime.fromObject({
        weekNumber: filters.weekFrom,
        weekYear: filters.yearFrom,
      }).toISO(),
      end: DateTime.fromObject({
        weekNumber: filters.weekTo,
        weekYear: filters.yearTo,
      }).toISO(),
    };
  }, [filters]);

  const formatedIntegratedCampaignReportData = useMemo(() => {
    if (!integratedCampaignReportData) {
      return {
        reached: [],
        withoutImpressions: [],
      };
    }

    const data = integratedCampaignReportData.integratedCampaignReport.map(
      (item) => {
        return {
          ...item,
          date: DateTime.fromISO(item.date).toLocaleString({
            month: "short",
            day: "numeric",
          }),
        };
      }
    );

    const [reached, withoutImpressions] = partition(data, (element) => {
      return (
        element.reached === IntegratedCampaignReportReached.AccountsReached
      );
    });

    const groupedReached = groupBy(reached, "date");
    const groupedWithoutImpressions = groupBy(withoutImpressions, "date");

    return {
      maxYValue: Math.max(
        ...Object.keys(groupedReached).map((key) => {
          const values = groupedReached[key];

          return values.reduce((acc, prev) => {
            return acc + prev.domainsCount;
          }, 0);
        }),
        ...Object.keys(groupedWithoutImpressions).map((key) => {
          const values = groupedWithoutImpressions[key];

          return values.reduce((acc, prev) => {
            return acc + prev.domainsCount;
          }, 0);
        })
      ),
      reached: Object.keys(groupedReached).map((key) => {
        return {
          groupLabel: key,
          groupItems: [
            {
              label: key,
              data: [
                {
                  label: BuyerResearchStage.Decision,
                  value:
                    groupedReached[key].find(
                      (el) => el.bestStage === BuyerResearchStageMap.Decision
                    )?.domainsCount || 0,
                },
                {
                  label: BuyerResearchStage.Consideration,
                  value:
                    groupedReached[key].find(
                      (el) =>
                        el.bestStage === BuyerResearchStageMap.Consideration
                    )?.domainsCount || 0,
                },
                {
                  label: BuyerResearchStage.Interest,
                  value:
                    groupedReached[key].find(
                      (el) => el.bestStage === BuyerResearchStageMap.Interest
                    )?.domainsCount || 0,
                },
                {
                  label: BuyerResearchStage.Awareness,
                  value:
                    groupedReached[key].find(
                      (el) => el.bestStage === BuyerResearchStageMap.Awareness
                    )?.domainsCount || 0,
                },
              ],
            },
          ],
        };
      }),
      withoutImpressions: Object.keys(groupedWithoutImpressions).map((key) => {
        return {
          groupLabel: key,
          groupItems: [
            {
              label: key,
              data: [
                {
                  label: BuyerResearchStage.Decision,
                  value:
                    groupedWithoutImpressions[key].find(
                      (el) => el.bestStage === BuyerResearchStageMap.Decision
                    )?.domainsCount || 0,
                },
                {
                  label: BuyerResearchStage.Consideration,
                  value:
                    groupedWithoutImpressions[key].find(
                      (el) =>
                        el.bestStage === BuyerResearchStageMap.Consideration
                    )?.domainsCount || 0,
                },
                {
                  label: BuyerResearchStage.Interest,
                  value:
                    groupedWithoutImpressions[key].find(
                      (el) => el.bestStage === BuyerResearchStageMap.Interest
                    )?.domainsCount || 0,
                },
                {
                  label: BuyerResearchStage.Awareness,
                  value:
                    groupedWithoutImpressions[key].find(
                      (el) => el.bestStage === BuyerResearchStageMap.Awareness
                    )?.domainsCount || 0,
                },
              ],
            },
          ],
        };
      }),
    };
  }, [integratedCampaignReportData]);

  const integratedCampaignReport = useExportableChart({
    title: "integratedCampaignReport",
    campaignId: Number(campaignId),
    dates,
    chart: (
      <>
        <StackedGroupedBarChart
          minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
          title="Accounts Reached with Display Ad Impressions"
          limitedWidth={!isLargerThan1800}
          isLoading={isLoadingCampaignIntegratedReportData}
          stretch
          data={formatedIntegratedCampaignReportData.reached}
          valueAxisLabel="Count of Accounts"
          maxYValue={formatedIntegratedCampaignReportData.maxYValue}
          hideGroupTicks={true}
          showTitle={true}
        />
        <StackedGroupedBarChart
          minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
          title="Accounts Not Reached with Display Ad Impressions"
          limitedWidth={!isLargerThan1800}
          isLoading={isLoadingCampaignIntegratedReportData}
          stretch
          data={formatedIntegratedCampaignReportData.withoutImpressions}
          valueAxisLabel="Count of Accounts"
          legendPosition={"bottom"}
          legendConfig={{
            domain: [
              BuyerResearchStage.Decision,
              BuyerResearchStage.Consideration,
              BuyerResearchStage.Interest,
              BuyerResearchStage.Awareness,
            ],
            range: getChartColors(colorMode, 0).reverse(),
          }}
          maxYValue={formatedIntegratedCampaignReportData.maxYValue}
          legendTitle="Stage"
          hideGroupTicks={true}
          showTitle={true}
        />
      </>
    ),
  });

  return (
    <Card
      onExpandedChange={setIsExpanded}
      isLoading={isLoadingCampaignIntegratedReportData}
      title={TITLE}
      noDataMessage="This visualization is designated for ad solution campaigns that are synced with an intent model."
      noDataIcon={MdAnalytics}
      hasData={!!integratedCampaignReportData?.integratedCampaignReport.length}
      minHeight={"200px"}
      isExpandable
    >
      {integratedCampaignReport.component}
      {integratedCampaignReport.exportComponent}
    </Card>
  );
};

export { IntegratedCampaignReport };
